import { z } from 'zod';
import { LeadStageCodeSchema } from './stages.js';

/**
 * @aiPurpose Lead task origin (#1076, extended #1359). `STAGE_PRIMARY`
 *   rows are historical/compatibility (carry `stageEntryCode`, remain
 *   valid/readable); stage transitions no longer create them — they only
 *   close existing active ones (Phase 2 #1956). `MANUAL` rows are
 *   manager-created; `CALLBACK_URGENT` rows are system-created urgent
 *   callbacks (e.g. a re-intake on a known phone). Append-only — mirrored
 *   by the Prisma `LeadTaskKind` enum and enforced by `check-enum-sync.sh`.
 */
export enum LeadTaskKind {
  STAGE_PRIMARY = 'STAGE_PRIMARY',
  MANUAL = 'MANUAL',
  CALLBACK_URGENT = 'CALLBACK_URGENT',
}
export const LeadTaskKindSchema = z.nativeEnum(LeadTaskKind);

export const LEAD_TASK_TITLE_MAX = 256;

/**
 * @aiPurpose wire projection of a `LeadTask` row. RBAC is the
 *   intersection of `tasks.read`/`tasks.manage` and Lead row
 *   visibility (#1075).
 * @aiInvariant STAGE_PRIMARY rows MUST carry a non-null
 *   `stageEntryCode`; MANUAL and CALLBACK_URGENT rows MUST leave it
 *   NULL. The DB enforces the same invariant via the
 *   `lead_tasks_kind_stage_entry_chk` CHECK constraint (widened for
 *   CALLBACK_URGENT in #1359).
 */
export const LeadTaskSchema = z
  .object({
    id: z.string().uuid(),
    leadId: z.string().uuid(),
    kind: LeadTaskKindSchema,
    /// Stage recorded on a historical `STAGE_PRIMARY` row (stage
    /// transitions no longer create primary tasks — Phase 2 #1956);
    /// NULL for `MANUAL`.
    stageEntryCode: LeadStageCodeSchema.nullable(),
    title: z.string().min(1).max(LEAD_TASK_TITLE_MAX),
    dueAt: z.string().datetime().nullable(),
    completedAt: z.string().datetime().nullable(),
    assigneeId: z.string().uuid().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  })
  .strict()
  .superRefine((v, ctx) => {
    if (v.kind === LeadTaskKind.STAGE_PRIMARY && v.stageEntryCode === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'STAGE_PRIMARY lead tasks must carry stageEntryCode',
        path: ['stageEntryCode'],
      });
      return;
    }
    if (
      (v.kind === LeadTaskKind.MANUAL || v.kind === LeadTaskKind.CALLBACK_URGENT) &&
      v.stageEntryCode !== null
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `${v.kind} lead tasks must not carry stageEntryCode`,
        path: ['stageEntryCode'],
      });
    }
  });
export type LeadTask = z.infer<typeof LeadTaskSchema>;

/**
 * @aiPurpose wire input for `POST /api/lead-tasks` (#1803) — a manager
 *   creating their own free-form MANUAL self-task. The backend forces
 *   `kind=MANUAL`, `stageEntryCode=NULL`, and `assigneeId=actor`, so the
 *   caller only chooses the target lead, the title, and an optional due
 *   anchor. `dueAt` omitted/NULL means "no SLA anchor" — the task stays
 *   out of the My Day queue and surfaces only in Attention.
 */
export const CreateLeadTaskInputSchema = z
  .object({
    leadId: z.string().uuid(),
    title: z.string().trim().min(1).max(LEAD_TASK_TITLE_MAX),
    dueAt: z.string().datetime().nullable().optional(),
  })
  .strict();
export type CreateLeadTaskInput = z.infer<typeof CreateLeadTaskInputSchema>;

/**
 * @aiPurpose wire input for `PATCH /api/lead-tasks/:id` (#1803) —
 *   rescheduling or renaming a MANUAL self-task. Both fields are optional
 *   so a caller can edit the title, the `dueAt` (NULL clears the anchor,
 *   removing the task from My Day), or both; the refine rejects an empty
 *   patch. The backend refuses non-MANUAL or completed rows.
 */
export const RescheduleLeadTaskInputSchema = z
  .object({
    title: z.string().trim().min(1).max(LEAD_TASK_TITLE_MAX).optional(),
    dueAt: z.string().datetime().nullable().optional(),
  })
  .strict()
  .refine((v) => v.title !== undefined || v.dueAt !== undefined, {
    message: 'At least one of title or dueAt must be provided',
  });
export type RescheduleLeadTaskInput = z.infer<typeof RescheduleLeadTaskInputSchema>;

/**
 * @aiPurpose wire query and response for `GET /api/lead-tasks?leadId=<uuid>`
 *   (#1863). Returns the full `LeadTask` projection for all OPEN tasks on
 *   a single lead. RBAC = `tasks.read` ∩ lead row-visibility: a caller
 *   without `leads.*` resolves to `scope='none'` and receives an empty
 *   list; a caller with visibility but targeting a lead outside their scope
 *   also receives an empty list (no existence leak). Mirrors the `myDay`
 *   read-path but scoped to one lead rather than the actor's full queue.
 */
export const GetLeadTasksQuerySchema = z.object({ leadId: z.string().uuid() }).strict();
export type GetLeadTasksQuery = z.infer<typeof GetLeadTasksQuerySchema>;

export const GetLeadTasksResponseSchema = z.object({ items: z.array(LeadTaskSchema) }).strict();
export type GetLeadTasksResponse = z.infer<typeof GetLeadTasksResponseSchema>;

/**
 * @aiPurpose wire projection of a SOFT-CANCELLED LeadTask (#2317 D-3a). The
 *   `LeadTask` fields PLUS a non-null `cancelledAt` ISO timestamp.
 * @aiInvariant DELIBERATELY a SEPARATE type from `LeadTask`: the OPEN-task wire
 *   response (`LeadTaskSchema`) MUST NOT gain a `cancelledAt` key — a deployed
 *   strict client that predates #2317 would reject an unexpected field — so
 *   cancelled rows travel on their own contract. Only MANUAL rows are ever
 *   cancelled, so `stageEntryCode` is always null here (the read enforces
 *   `kind=MANUAL`).
 */
export const CancelledLeadTaskSchema = z
  .object({
    id: z.string().uuid(),
    leadId: z.string().uuid(),
    kind: LeadTaskKindSchema,
    stageEntryCode: LeadStageCodeSchema.nullable(),
    title: z.string().min(1).max(LEAD_TASK_TITLE_MAX),
    dueAt: z.string().datetime().nullable(),
    completedAt: z.string().datetime().nullable(),
    assigneeId: z.string().uuid().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    cancelledAt: z.string().datetime(),
  })
  .strict();
export type CancelledLeadTask = z.infer<typeof CancelledLeadTaskSchema>;

/**
 * @aiPurpose response for `GET /api/lead-tasks/cancelled?leadId=<uuid>` (#2317
 *   D-3a). Reuses `GetLeadTasksQuerySchema` for the query. RBAC = `tasks.read`
 *   ∩ lead visibility; a manager sees only their OWN cancelled MANUAL tasks,
 *   admin sees all cancelled tasks on any visible lead. Ordered `cancelledAt`
 *   DESC (most-recently cancelled first).
 */
export const GetCancelledLeadTasksResponseSchema = z
  .object({ items: z.array(CancelledLeadTaskSchema) })
  .strict();
export type GetCancelledLeadTasksResponse = z.infer<typeof GetCancelledLeadTasksResponseSchema>;
